"use client";

import { useState } from "react";
import Link from "next/link";

export default function FooterCTASection() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    // Backend connection point for email capture
    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: "linear-gradient(160deg, #1E4D6B 0%, #2D6A8F 50%, #3D6B52 100%)" }}>
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-20"
          style={{ background: "radial-gradient(ellipse, rgba(232, 165, 152, 0.5) 0%, transparent 70%)" }} />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full opacity-15"
          style={{ background: "radial-gradient(ellipse, rgba(255,255,255,0.3) 0%, transparent 70%)" }} />
        {/* Subtle flower/heart pattern */}
        <svg className="absolute right-8 top-8 opacity-10" width="200" height="200" viewBox="0 0 200 200" fill="none" aria-hidden="true">
          <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="1"/>
          <circle cx="100" cy="100" r="50" stroke="white" strokeWidth="1"/>
          <circle cx="100" cy="100" r="20" stroke="white" strokeWidth="1"/>
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
            <ellipse key={deg} cx="100" cy="100" rx="12" ry="30" stroke="white" strokeWidth="0.8"
              transform={`rotate(${deg} 100 100) translate(0 -50)`} />
          ))}
        </svg>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Headline */}
        <div className="space-y-6 mb-12">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest"
            style={{ background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.2)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-soft inline-block" />
            Start Today — It's Free
          </span>
          <h2 className="font-heading font-bold text-white" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            Ready to build{" "}
            <span style={{ color: "#F5C9C0" }}>your family?</span>
          </h2>
          <p className="text-lg text-white/75 leading-relaxed max-w-xl mx-auto">
            Join thousands of families who chose love over loans. Create your private campaign in minutes, share it only with people you trust.
          </p>
        </div>

        {/* Sign up form */}
        {!submitted ? (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-8">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="flex-1 px-5 py-4 rounded-full text-sm font-medium outline-none transition-all duration-200"
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "1.5px solid rgba(255,255,255,0.25)",
                color: "white",
                backdropFilter: "blur(10px)",
              }}
              aria-label="Email address"
            />
            <button type="submit" className="btn-accent text-sm px-7 py-4 whitespace-nowrap flex-shrink-0">
              Get Started Free
            </button>
          </form>
        ) : (
          <div className="flex items-center justify-center gap-3 py-4 mb-8">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "rgba(90, 138, 111, 0.3)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8BB89A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <p className="text-white font-medium">You're on the list! We'll be in touch soon. 💛</p>
          </div>
        )}

        {/* Or CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link href="/create-campaign"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm text-white border-2 border-white/40 hover:border-white hover:bg-white/10 transition-all duration-300">
            Create Campaign Now
          </Link>
          <Link href="/sign-up"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm transition-all duration-300"
            style={{ background: "rgba(255,255,255,0.95)", color: "#2D6A8F" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            Sign Up Free
          </Link>
        </div>

        {/* Trust micro-copy */}
        <p className="mt-8 text-xs text-white/50">
          No credit card required · Private by default · Cancel anytime
        </p>
      </div>
    </section>
  );
}